/**
 * Reconnect backoff (HIG-5970, design §5). Exponential with jitter, abort
 * aware, on the neverthrow channel — retries an `Err` result, never a throw.
 * Deliberately tiny; if retry policies multiply this is the tripwire to
 * revisit a real scheduler (design doc, open question 4).
 */
import { Result, ResultAsync } from 'neverthrow'

export type BackoffOptions = {
  readonly retries: number
  readonly baseMs: number
  readonly maxMs?: number
  readonly signal?: AbortSignal
}

export type BackoffDelayOptions = Pick<BackoffOptions, 'baseMs' | 'maxMs' | 'signal'>

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      resolve()
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

export function waitForBackoff(attempt: number, opts: BackoffDelayOptions): Promise<void> {
  const ceiling = Math.min(opts.baseMs * 2 ** Math.max(0, attempt - 1), opts.maxMs ?? Number.POSITIVE_INFINITY)
  return sleep(ceiling * (0.5 + Math.random() / 2), opts.signal)
}

export function withBackoff<T, E>(run: () => ResultAsync<T, E>, opts: BackoffOptions): ResultAsync<T, E> {
  const attempt = async (n: number): Promise<Result<T, E>> => {
    const result = await run()
    if (result.isOk() || n >= opts.retries || opts.signal?.aborted) return result
    await waitForBackoff(n + 1, opts)
    if (opts.signal?.aborted) return result
    return attempt(n + 1)
  }
  return new ResultAsync(attempt(0))
}
